import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { functions, db } from './firebase';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import mapboxgl from 'mapbox-gl';
import polyline from '@mapbox/polyline';
import 'mapbox-gl/dist/mapbox-gl.css';
import './CreatePage.css';

const MAPBOX_ACCESS_TOKEN = 'pk.eyJ1IjoiZGFuaWVsa2FsZG9yIiwiYSI6ImNtYjRyNTYzczA1emcya3FwdzhoMmhmdGIifQ.qkeEKxO6j5ugrpbdC06grA';

// Colors for multi-activity routes
const ROUTE_COLORS = ['#FC5200', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444'];

const CreatePage = ({ user }) => {
    const navigate = useNavigate();

    // Activity state
    const [activities, setActivities] = useState([]);
    const [selectedActivities, setSelectedActivities] = useState([]);
    const [isLoadingActivities, setIsLoadingActivities] = useState(false);
    const [stravaConnected, setStravaConnected] = useState(false);

    // Template state
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

    // Generation state
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState('');

    // Map preview
    const mapContainer = useRef(null);
    const map = useRef(null);

    // Load activities
    const loadActivities = useCallback(async () => {
        if (!user) return;
        setIsLoadingActivities(true);
        try {
            const tokenDocRef = doc(db, 'users', user.uid, 'stravaTokens', 'tokens');
            const docSnap = await getDoc(tokenDocRef);
            if (docSnap.exists()) {
                setStravaConnected(true);
                const getActivitiesFunc = httpsCallable(functions, 'getStravaActivities');
                const result = await getActivitiesFunc();
                if (result.data) {
                    setActivities(Array.isArray(result.data) ? result.data : []);
                }
            } else {
                setStravaConnected(false);
            }
        } catch (err) {
            console.error('Error loading activities:', err);
        } finally {
            setIsLoadingActivities(false);
        }
    }, [user]);

    // Load templates
    const loadTemplates = useCallback(async () => {
        if (!user) return;
        setIsLoadingTemplates(true);
        try {
            const getTemplatesFunc = httpsCallable(functions, 'getPromptTemplates');
            const result = await getTemplatesFunc();
            if (result.data?.templates && Array.isArray(result.data.templates)) {
                setTemplates(result.data.templates.filter(t => t.enabled !== false));
            }
        } catch (err) {
            console.error('Error loading templates:', err);
        } finally {
            setIsLoadingTemplates(false);
        }
    }, [user]);

    useEffect(() => {
        loadActivities();
        loadTemplates();
    }, [loadActivities, loadTemplates]);

    // Toggle activity selection
    const toggleActivity = (activity) => {
        setSelectedActivities(prev => {
            const isSelected = prev.some(a => a.id === activity.id);
            if (isSelected) {
                return prev.filter(a => a.id !== activity.id);
            }
            return [...prev, activity];
        });
    };

    // Get the color assigned to a selected activity
    const getActivityColor = (activityId) => {
        const index = selectedActivities.findIndex(a => a.id === activityId);
        return index >= 0 ? ROUTE_COLORS[index % ROUTE_COLORS.length] : null;
    };

    // Update map preview when selected activities change
    useEffect(() => {
        if (!mapContainer.current || selectedActivities.length === 0) {
            if (map.current) {
                map.current.remove();
                map.current = null;
            }
            return;
        }

        // Clean up existing map
        if (map.current) {
            map.current.remove();
            map.current = null;
        }

        mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: 'mapbox://styles/mapbox/streets-v12',
            center: [0, 0],
            zoom: 1,
            interactive: true,
            attributionControl: false,
        });

        map.current.on('load', () => {
            const bounds = new mapboxgl.LngLatBounds();

            selectedActivities.forEach((activity, index) => {
                if (!activity.map?.summary_polyline) return;

                const coordinates = polyline.decode(activity.map.summary_polyline)
                    .map(coord => [coord[1], coord[0]]);

                coordinates.forEach(coord => bounds.extend(coord));

                const sourceId = `route-${activity.id}`;
                const layerId = `route-layer-${activity.id}`;
                const color = ROUTE_COLORS[index % ROUTE_COLORS.length];

                map.current.addSource(sourceId, {
                    type: 'geojson',
                    data: {
                        type: 'Feature',
                        properties: {},
                        geometry: { type: 'LineString', coordinates },
                    },
                });

                map.current.addLayer({
                    id: layerId,
                    type: 'line',
                    source: sourceId,
                    layout: { 'line-join': 'round', 'line-cap': 'round' },
                    paint: {
                        'line-color': color,
                        'line-width': 3,
                        'line-opacity': 0.85,
                    },
                });
            });

            if (!bounds.isEmpty()) {
                map.current.fitBounds(bounds, { padding: 40 });
            }
        });

        return () => {
            if (map.current) {
                map.current.remove();
                map.current = null;
            }
        };
    }, [selectedActivities]);

    // Handle generation
    const handleGenerate = async () => {
        if (!user || selectedActivities.length === 0 || !selectedTemplate) return;

        setIsGenerating(true);
        setError('');

        try {
            const requestImageGenerationFunc = httpsCallable(functions, 'requestImageGeneration');

            const stravaIds = selectedActivities.map(a => a.id.toString());

            // Aggregate stats
            const totalDistance = selectedActivities.reduce((sum, a) => sum + (a.distance || 0), 0);
            const totalMovingTime = selectedActivities.reduce((sum, a) => sum + (a.moving_time || 0), 0);
            const totalElevation = selectedActivities.reduce((sum, a) => sum + (a.total_elevation_gain || 0), 0);

            const requestPayload = {
                StravaIds: stravaIds,
                isMultiActivity: true,
                StravaId: stravaIds[0], // Primary for backwards compat
                promptTemplateId: selectedTemplate.id,
                gpxDataSource: 'polyline',
                stravaStats: {
                    activityName: selectedActivities.map(a => a.name).join(' + '),
                    activityType: selectedActivities[0].type,
                    distance: totalDistance,
                    movingTime: totalMovingTime,
                    elevationGain: totalElevation,
                    activityDate: { seconds: new Date(selectedActivities[0].start_date).getTime() / 1000 },
                },
            };

            const result = await requestImageGenerationFunc(requestPayload);
            navigate(`/map/${result.data.requestId}`);
        } catch (e) {
            console.error('Error generating map:', e);
            if (e.code === 'functions/resource-exhausted') {
                setError('You have run out of credits.');
            } else if (e.code === 'functions/already-exists') {
                setError('You already have a map being generated. Please wait for it to complete.');
            } else {
                setError(e.message || 'An error occurred.');
            }
            setIsGenerating(false);
        }
    };

    // Format helpers
    const formatDistance = (meters) => {
        if (!meters) return '0 km';
        return `${(meters / 1000).toFixed(1)} km`;
    };

    const formatDuration = (seconds) => {
        if (!seconds) return '0m';
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        return h > 0 ? `${h}h ${m}m` : `${m}m`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric'
        });
    };

    if (!user) {
        return (
            <div className="create-page">
                <div className="create-page-message">
                    <h2>Sign in to create a multi-activity map</h2>
                    <p>Connect your Strava account to get started.</p>
                </div>
            </div>
        );
    }

    if (!stravaConnected && !isLoadingActivities) {
        return (
            <div className="create-page">
                <div className="create-page-message">
                    <h2>Connect Strava</h2>
                    <p>Connect your Strava account to select activities for your map.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="create-page">
            <div className="create-header">
                <h1>Create Multi-Activity Map</h1>
                <p>Select multiple activities to combine into a single artistic map.</p>
            </div>

            <div className="create-layout">
                {/* Left: Activity selection */}
                <div className="create-activities-panel">
                    <h3>
                        Select Activities
                        {selectedActivities.length > 0 && (
                            <span className="selected-count"> ({selectedActivities.length} selected)</span>
                        )}
                    </h3>

                    {isLoadingActivities ? (
                        <div className="create-loading">Loading activities...</div>
                    ) : (
                        <div className="create-activities-list">
                            {activities.map(activity => {
                                const isSelected = selectedActivities.some(a => a.id === activity.id);
                                const color = getActivityColor(activity.id);
                                return (
                                    <div
                                        key={activity.id}
                                        className={`create-activity-card ${isSelected ? 'selected' : ''}`}
                                        onClick={() => toggleActivity(activity)}
                                    >
                                        {isSelected && (
                                            <div
                                                className="activity-color-indicator"
                                                style={{ backgroundColor: color }}
                                            />
                                        )}
                                        <div className="activity-card-content">
                                            <div className="activity-card-name">{activity.name}</div>
                                            <div className="activity-card-meta">
                                                <span>{formatDate(activity.start_date)}</span>
                                                <span>{formatDistance(activity.distance)}</span>
                                                <span>{formatDuration(activity.moving_time)}</span>
                                            </div>
                                        </div>
                                        <div className="activity-card-checkbox">
                                            {isSelected ? '\u2713' : ''}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Right: Map preview + template + generate */}
                <div className="create-preview-panel">
                    {/* Map preview */}
                    <div className="create-map-preview">
                        {selectedActivities.length > 0 ? (
                            <div ref={mapContainer} className="create-map-container" />
                        ) : (
                            <div className="create-map-placeholder">
                                Select activities to preview routes on the map
                            </div>
                        )}
                    </div>

                    {/* Aggregated stats */}
                    {selectedActivities.length > 0 && (
                        <div className="create-stats">
                            <div className="stat-item">
                                <span className="stat-label">Activities</span>
                                <span className="stat-value">{selectedActivities.length}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Total Distance</span>
                                <span className="stat-value">
                                    {formatDistance(selectedActivities.reduce((sum, a) => sum + (a.distance || 0), 0))}
                                </span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Total Time</span>
                                <span className="stat-value">
                                    {formatDuration(selectedActivities.reduce((sum, a) => sum + (a.moving_time || 0), 0))}
                                </span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Total Elevation</span>
                                <span className="stat-value">
                                    {Math.round(selectedActivities.reduce((sum, a) => sum + (a.total_elevation_gain || 0), 0))}m
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Template selection */}
                    {selectedActivities.length > 0 && (
                        <div className="create-templates">
                            <h3>Choose Style</h3>
                            {isLoadingTemplates ? (
                                <div className="create-loading">Loading styles...</div>
                            ) : (
                                <div className="create-template-grid">
                                    {templates.map(template => (
                                        <div
                                            key={template.id}
                                            className={`create-template-card ${selectedTemplate?.id === template.id ? 'selected' : ''}`}
                                            onClick={() => setSelectedTemplate(template)}
                                        >
                                            {template.previewImageUrl && (
                                                <img
                                                    src={template.previewImageUrl}
                                                    alt={template.name}
                                                    className="template-preview-img"
                                                />
                                            )}
                                            <span className="template-name">{template.name}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Generate button */}
                    {error && <div className="create-error">{error}</div>}

                    <button
                        className="create-generate-btn"
                        onClick={handleGenerate}
                        disabled={selectedActivities.length < 2 || !selectedTemplate || isGenerating}
                    >
                        {isGenerating
                            ? 'Generating...'
                            : selectedActivities.length < 2
                                ? 'Select at least 2 activities'
                                : !selectedTemplate
                                    ? 'Choose a style'
                                    : `Generate Map (${selectedActivities.length} activities)`
                        }
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreatePage;
